import { api } from '@/core/api/request'
import { logger } from '@/core/app/logger'
import {
  type CheckoutPaymentDetail,
  CheckoutPaymentDetailSchema,
  type CheckoutSessionResponse,
  CheckoutSessionResponseSchema,
  type CreateCheckoutSessionRequest,
  type ListProductsParams,
  type ProductListResponse,
  ProductListResponseSchema,
  type TicketOrderListResponse,
  TicketOrderListResponseSchema,
} from '@/schemas/v2/ec'

const BASE = process.env.NEXT_PUBLIC_API_URL ?? ''

type FetchOptions = { headers?: Record<string, string> }

/** 商品一覧を取得する（GET /api/v2/products）。params.productType 指定時は当該種別のみに絞り込む。 */
export async function fetchProducts(params?: ListProductsParams, options?: FetchOptions): Promise<ProductListResponse> {
  const query = params?.productType ? `?${new URLSearchParams({ productType: params.productType }).toString()}` : ''
  const url = `${BASE}/api/v2/products${query}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return ProductListResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

/** Stripe Checkout セッションを作成する（POST /api/v2/ticket/checkout/sessions）。 */
export async function createCheckoutSession(
  body: CreateCheckoutSessionRequest,
  options?: FetchOptions,
): Promise<CheckoutSessionResponse> {
  const url = `${BASE}/api/v2/ticket/checkout/sessions`
  logger.info({ fetch_method: 'POST', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.post<unknown>(url, body, options)
    return CheckoutSessionResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'POST', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

/** Checkout Session ID をキーに決済詳細（商品情報含む）を取得する（GET /api/v2/ticket/checkout/sessions/{sessionId}）。 */
export async function fetchCheckoutSessionDetail(
  sessionId: string,
  options?: FetchOptions,
): Promise<CheckoutPaymentDetail> {
  const url = `${BASE}/api/v2/ticket/checkout/sessions/${encodeURIComponent(sessionId)}`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return CheckoutPaymentDetailSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}

/** マイ注文・保有チケット一覧を取得する（GET /api/v2/ticket/orders）。 */
export async function fetchTicketOrders(options?: FetchOptions): Promise<TicketOrderListResponse> {
  const url = `${BASE}/api/v2/ticket/orders`
  logger.info({ fetch_method: 'GET', fetch_url: url, msg: 'fetch 開始' })
  try {
    const data = await api.get<unknown>(url, options)
    return TicketOrderListResponseSchema.parse(data)
  } catch (e) {
    logger.error({ fetch_method: 'GET', fetch_url: url, msg: `fetch エラー: ${String(e)}` })
    throw e
  }
}
